import * as React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
  Divider,
} from '@mui/material';
import dayjs from 'dayjs';

export interface MissingDateRange {
  from: Date;
  to: Date;
}

export interface MissingDataForEntity {
  entityLabel: string;
  missingDates: MissingDateRange[];
}

export interface MissingDataDialogProps {
  open: boolean;
  onClose: () => void;
  onProceed?: () => void;
  missingData: MissingDataForEntity[];
}

/**
 * Formatta un range di date in una stringa leggibile
 */
const formatDateRange = (from: Date, to: Date): string => {
  // Se le date sono identiche (stessa data e ora), mostra solo una volta
  if (dayjs(from).isSame(to, 'minute')) {
    return dayjs(from).format('DD/MM/YYYY HH:mm');
  }

  const fromFormatted = dayjs(from).format('DD/MM/YYYY HH:mm');
  const toFormatted = dayjs(to).format('DD/MM/YYYY HH:mm');

  // Se le date sono nello stesso giorno, mostra solo una volta la data
  if (dayjs(from).isSame(to, 'day')) {
    const fromTime = dayjs(from).format('HH:mm');
    const toTime = dayjs(to).format('HH:mm');
    return `${dayjs(from).format('DD/MM/YYYY')} ${fromTime} - ${toTime}`;
  }

  return `${fromFormatted} - ${toFormatted}`;
};

/**
 * Raggruppa date consecutive in range
 */
const groupConsecutiveDates = (dates: Date[]): MissingDateRange[] => {
  if (dates.length === 0) return [];

  // Ordina le date
  const sortedDates = [...dates].sort((a, b) => a.getTime() - b.getTime());

  const ranges: MissingDateRange[] = [];
  let currentRangeStart = sortedDates[0];
  let currentRangeEnd = sortedDates[0];

  for (let i = 1; i < sortedDates.length; i++) {
    const currentDate = sortedDates[i];
    const prevDate = sortedDates[i - 1];

    // Calcola la differenza in ore tra le date consecutive
    const hoursDiff = (currentDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60);

    // Se la differenza è esattamente 1 ora, sono consecutive
    if (hoursDiff === 1) {
      currentRangeEnd = currentDate;
    } else {
      // Salva il range corrente e inizia uno nuovo
      ranges.push({
        from: currentRangeStart,
        to: currentRangeEnd,
      });
      currentRangeStart = currentDate;
      currentRangeEnd = currentDate;
    }
  }

  // Aggiungi l'ultimo range
  ranges.push({
    from: currentRangeStart,
    to: currentRangeEnd,
  });

  return ranges;
};

export const MissingDataDialog: React.FC<MissingDataDialogProps> = ({
  open,
  onClose,
  onProceed,
  missingData,
}) => {
  const handleProceed = () => {
    if (onProceed) {
      onProceed();
    }
    onClose();
  };

  const totalMissingCount = missingData.reduce((sum, item) => sum + item.missingDates.length, 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Campi obbligatori mancanti</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {totalMissingCount > 0 ? (
            <>
              Sono presenti dati mancanti per <strong>{missingData.length}</strong>{' '}
              {missingData.length === 1 ? 'POD' : 'POD'}:
            </>
          ) : (
            <>Sono presenti dati mancanti:</>
          )}
        </DialogContentText>
        <Box
          sx={{
            maxHeight: '500px',
            overflow: 'auto',
            border: theme => `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            px: 2,
          }}
        >
          {missingData.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Nessun dato mancante.
            </Typography>
          ) : (
            <List dense>
              {missingData.map((entityData, entityIndex) => (
                <React.Fragment key={entityIndex}>
                  {entityIndex > 0 && <Divider sx={{ my: 1 }} />}
                  <ListItem sx={{ px: 0, flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ width: '100%' }}>
                      {entityData.entityLabel}
                    </Typography>
                    <Box sx={{ width: '100%', pl: 2 }}>
                      {entityData.missingDates.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          Nessun dato mancante.
                        </Typography>
                      ) : (
                        <List dense>
                          {entityData.missingDates.map((range, rangeIndex) => (
                            <ListItem key={rangeIndex} sx={{ px: 0, py: 0.25 }}>
                              <ListItemText
                                primary={formatDateRange(range.from, range.to)}
                                primaryTypographyProps={{
                                  variant: 'body2',
                                }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </Box>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Box sx={{ pb: 2, pr: 2 }}>
          <Button onClick={onClose}>Annulla</Button>
          {onProceed && (
            <Button sx={{ ml: 2 }} onClick={handleProceed} variant="contained">
              Procedi
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Utility function per raggruppare date mancanti in range
 */
export const groupMissingDates = (dates: Date[]): MissingDateRange[] => {
  return groupConsecutiveDates(dates);
};
